import { css, keyframes } from "@emotion/react";
import Link from "next/link";
import { FC } from "react";

export type ExternalSiteData = {
  ogHref: string;
  ogSrc: string;
  ogTitle: string;
  ogDescription: string;
};

export type Props = {
  url: string;
  data?: ExternalSiteData;
};

const OgpLink: FC<Props> = ({ url, data }) => {
  const isLoading = data === undefined;

  if (isLoading) {
    return (
      <div>
        <Link href={url}>{url}</Link>
        <div css={containerStyle}>
          <div css={skeletonImageStyle} />
          <div css={textContainerStyle}>
            {[...Array(3).keys()].map((_, i) => {
              return <div key={`skeletonText-${i}`} css={skeletonTextStyle} />;
            })}
          </div>
        </div>
      </div>
    );
  }

  const { ogHref, ogTitle, ogSrc, ogDescription } = data;

  return (
    <div>
      <a href={url} target="_blank" rel="noopener noreferrer">
        {url}
      </a>
      <a
        href={ogHref}
        target="_blank"
        rel="noopener noreferrer"
        css={containerStyle}
      >
        <img src={ogSrc} alt={ogTitle} css={imageStyle} />
        <span css={textContainerStyle}>
          <span css={urlStyle}>{ogHref}</span>
          <span css={textDescriptionStyle}>{ogDescription}</span>
        </span>
      </a>
    </div>
  );
};

const skeletonAnimation = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

const containerStyle = css`
  margin-top: 0.25rem;
  display: flex;
  height: 120px;
  width: 360px;
  border: 1px solid #4e4e4e;
  border-radius: 4px;
  overflow: hidden;
`;

const imageStyle = css`
  height: 120px;
  width: 120px;
  flex: 0;
  border-right: 1px solid #4e4e4e;
  object-fit: cover;
`;

const skeletonImageStyle = css`
  aspect-ratio: 1 / 1;
  background-color: #313131;
  overflow: hidden;
  position: relative;

  &::before {
    animation: ${skeletonAnimation} 2s infinite ease;
    background: linear-gradient(
      90deg,
      transparent,
      rgb(255 255 255 / 0.2),
      transparent
    );
    content: "";
    height: 120px;
    left: 0;
    position: absolute;
    top: 0;
    width: 120px;
  }
`;

const skeletonTextStyle = css`
  background-color: #313131;
  height: 16px;
  overflow: hidden;
  position: relative;

  &::before {
    animation: ${skeletonAnimation} 2s infinite ease;
    background: linear-gradient(
      90deg,
      transparent,
      rgb(255 255 255 / 0.2),
      transparent
    );
    content: "";
    height: 240px;
    left: 0;
    position: absolute;
    top: 0;
    width: 120px;
  }
`;

const textContainerStyle = css`
  width: 240px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.5rem;
`;

const urlStyle = css`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  font-size: 0.75rem;
`;

const textDescriptionStyle = css`
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  font-size: 0.75rem;
`;

export default OgpLink;
